import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Shield, ShieldCheck, User, Loader2, X } from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';
import * as adminApi from '../../utils/adminApi';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const UsersManagement = () => {
  const { token } = useAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin',
    permissions: []
  });

  const allPermissions = ['orders', 'services', 'portfolio', 'testimonials', 'messages', 'users', 'clients'];

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getAdminUsers(token);
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createAdminUser(token, formData);
      toast.success('User created successfully');
      setShowModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        email: formData.email,
        role: formData.role,
        permissions: formData.permissions
      };
      await adminApi.updateAdminUser(token, editingUser.admin_id, updateData);
      toast.success('User updated successfully');
      setShowModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (adminId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) return;
    
    try {
      await adminApi.deleteAdminUser(token, adminId);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'admin',
      permissions: []
    });
    setEditingUser(null);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role || 'admin',
      permissions: user.permissions || []
    });
    setShowModal(true);
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-[#D4AF37] text-[#141619]',
      admin: 'bg-blue-500/20 text-blue-400',
      editor: 'bg-green-500/20 text-green-400'
    };
    return colors[role] || colors.admin;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-[#D4AF37]" />
            User Management
          </h1>
          <p className="text-[#9CA3AF] mt-1">Manage admin users and permissions</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users Table */}
      <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#2a2d32]">
              <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium text-sm uppercase tracking-wider">User</th>
              <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium text-sm uppercase tracking-wider">Role</th>
              <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium text-sm uppercase tracking-wider">Permissions</th>
              <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium text-sm uppercase tracking-wider">Status</th>
              <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium text-sm uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.admin_id} className="border-b border-[#2a2d32] last:border-0 hover:bg-[#2a2d32]/50">
                <td className="py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2a2d32] flex items-center justify-center">
                      {user.role === 'super_admin' ? (
                        <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                      ) : (
                        <User className="w-5 h-5 text-[#9CA3AF]" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.username}</p>
                      <p className="text-sm text-[#6B7280]">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                    {user.role || 'admin'}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <div className="flex flex-wrap gap-1">
                    {(user.permissions || []).slice(0, 3).map(perm => (
                      <span key={perm} className="px-2 py-0.5 bg-[#2a2d32] rounded text-xs text-[#9CA3AF]">
                        {perm}
                      </span>
                    ))}
                    {(user.permissions || []).length > 3 && (
                      <span className="px-2 py-0.5 bg-[#2a2d32] rounded text-xs text-[#9CA3AF]">
                        +{user.permissions.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-4 px-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    user.active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {user.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEditModal(user)}
                      className="p-2 hover:bg-[#2a2d32] rounded-lg transition-colors"
                      data-testid={`edit-user-${user.admin_id}`}
                    >
                      <Edit className="w-4 h-4 text-[#9CA3AF] hover:text-[#D4AF37]" />
                    </button>
                    {user.role !== 'super_admin' && (
                      <button
                        onClick={() => handleDeleteUser(user.admin_id, user.username)}
                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid={`delete-user-${user.admin_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-[#9CA3AF] hover:text-red-500" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan="5" className="py-12 text-center text-[#6B7280]">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#2a2d32]">
              <h2 className="text-xl font-bold text-white">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 hover:bg-[#2a2d32] rounded-lg"
              >
                <X className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>
            
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Username</label>
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  disabled={!!editingUser}
                  required={!editingUser}
                  className="bg-[#141619] border-[#2a2d32] text-white"
                  data-testid="user-username-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="bg-[#141619] border-[#2a2d32] text-white"
                  data-testid="user-email-input"
                />
              </div>
              
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required={!editingUser}
                    className="bg-[#141619] border-[#2a2d32] text-white"
                    data-testid="user-password-input"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 bg-[#141619] border border-[#2a2d32] rounded-lg text-white"
                  data-testid="user-role-select"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Permissions</label>
                <div className="flex flex-wrap gap-2">
                  {allPermissions.map(perm => (
                    <button
                      key={perm}
                      type="button"
                      onClick={() => togglePermission(perm)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        formData.permissions.includes(perm)
                          ? 'bg-[#D4AF37] text-[#141619]'
                          : 'bg-[#2a2d32] text-[#9CA3AF] hover:bg-[#3a3d42]'
                      }`}
                    >
                      {perm}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 border-[#2a2d32] text-white hover:bg-[#2a2d32]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]"
                  data-testid="save-user-btn"
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
